import { useEffect, useState } from 'react';
import { Plus, MagnifyingGlass, PencilSimple, Archive, TestTube, X, ShieldStar } from '@phosphor-icons/react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { SUPPORTED_LOCATIONS } from '@/config/locations';

const COUNTRY_OPTIONS = ['*', ...SUPPORTED_LOCATIONS.map((c) => c.name)];

const CountrySelect = ({ value, onChange, allowWildcard = true, testId }) => (
  <select
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100"
    data-testid={testId}
  >
    <option value="">Select country…</option>
    {(allowWildcard ? COUNTRY_OPTIONS : COUNTRY_OPTIONS.filter((c) => c !== '*')).map((c) => (
      <option key={c} value={c}>{c === '*' ? '* (Any country)' : c}</option>
    ))}
  </select>
);

const STATUSES = ['allowed', 'restricted', 'prohibited'];
const STATUS_TONE = {
  allowed: 'bg-green-100 text-green-700',
  restricted: 'bg-yellow-100 text-yellow-700',
  prohibited: 'bg-red-100 text-red-700',
};

const empty = {
  origin: '*', destination: '*', category: '', status: 'allowed',
  notes: '', weight_limit_kg: '', quantity_limit: '',
  required_documents: '', required_permits: '',
  internal_notes: '', source_reference: '', effective_date: '',
};

const RulesEditor = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ origin: '', destination: '', category: '', status: '' });
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | rule object
  const [simulator, setSimulator] = useState(null); // {origin,destination,category,weight,quantity,result}

  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => { if (!cancelled) setLoading(true); });
    const params = new URLSearchParams();
    if (filter.origin) params.set('origin', filter.origin);
    if (filter.destination) params.set('destination', filter.destination);
    if (filter.category) params.set('category', filter.category);
    if (filter.status) params.set('status', filter.status);
    if (includeArchived) params.set('include_archived', 'true');
    fetch(`${API}/admin/compliance/rules?${params}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setRules(data.rules || []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filter, includeArchived, refreshTick]);

  const loadRules = () => setRefreshTick((t) => t + 1);

  const handleSave = async (payload) => {
    const body = {
      ...payload,
      weight_limit_kg: payload.weight_limit_kg === '' ? null : parseFloat(payload.weight_limit_kg),
      quantity_limit: payload.quantity_limit === '' ? null : parseInt(payload.quantity_limit),
      required_documents: typeof payload.required_documents === 'string'
        ? payload.required_documents.split(',').map((s) => s.trim()).filter(Boolean) : payload.required_documents,
      required_permits: typeof payload.required_permits === 'string'
        ? payload.required_permits.split(',').map((s) => s.trim()).filter(Boolean) : payload.required_permits,
    };
    const isNew = editing === 'new' || !payload.rule_id;
    const url = isNew ? `${API}/admin/compliance/rules` : `${API}/admin/compliance/rules/${payload.rule_id}`;
    const res = await fetch(url, {
      method: isNew ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const e = await res.json();
      toast.error(typeof e.detail === 'string' ? e.detail : 'Save failed');
      return;
    }
    toast.success(isNew ? 'Rule created' : 'Rule updated');
    setEditing(null);
    loadRules();
  };

  const handleArchive = async (rule_id) => {
    if (!window.confirm('Archive this rule? It will no longer apply to validations.')) return;
    await fetch(`${API}/admin/compliance/rules/${rule_id}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Rule archived');
    loadRules();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-3xl font-extrabold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="rules-editor-title">
          Restricted Goods Rules Editor
        </h1>
        <div className="flex gap-2">
          <Button onClick={() => setSimulator({ origin: '', destination: '', category: '', weight: 1, quantity: 1, result: null })}
                  className="bg-white text-gray-900 border border-gray-200 hover:border-[#6D28D9]/50" data-testid="open-simulator">
            <TestTube size={16} weight="bold" className="mr-1" /> Simulate
          </Button>
          <Button onClick={() => setEditing({ ...empty })} className="dropio-gradient-cta text-white" data-testid="new-rule">
            <Plus size={16} weight="bold" className="mr-1" /> New Rule
          </Button>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-6">Primary source of truth. AI advises; rules engine decides.</p>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="rules-filters">
        <CountrySelect value={filter.origin} onChange={(v) => setFilter({ ...filter, origin: v === '*' ? '' : v })} testId="filter-origin" />
        <CountrySelect value={filter.destination} onChange={(v) => setFilter({ ...filter, destination: v === '*' ? '' : v })} testId="filter-destination" />
        <Input placeholder="Category" value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} data-testid="filter-category" />
        <select className="h-10 px-3 rounded-md border border-gray-200 bg-white text-sm" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="filter-status">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="include-archived" />
          Show archived
        </label>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
              <th className="px-4 py-3">Route</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Limits</th>
              <th className="px-4 py-3">Required Docs</th>
              <th className="px-4 py-3">Notes</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody data-testid="rules-table">
            {loading && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
            )}
            {!loading && rules.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400">No rules match these filters.</td></tr>
            )}
            {!loading && rules.map((r) => (
              <tr key={r.rule_id} className={`border-t border-gray-100 ${r.is_archived ? 'opacity-50' : ''}`} data-testid={`rule-row-${r.rule_id}`}>
                <td className="px-4 py-3 font-mono text-xs">{r.origin} → {r.destination}</td>
                <td className="px-4 py-3 capitalize font-semibold">{r.category}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_TONE[r.status]}`}>{r.status}</span></td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {r.weight_limit_kg && <div>≤ {r.weight_limit_kg} kg</div>}
                  {r.quantity_limit && <div>≤ {r.quantity_limit} units</div>}
                  {!r.weight_limit_kg && !r.quantity_limit && '—'}
                </td>
                <td className="px-4 py-3 text-xs">{(r.required_documents || []).join(', ') || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-600 max-w-xs truncate" title={r.notes}>{r.notes || '—'}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(r)} className="text-[#6D28D9] hover:underline mr-3" data-testid={`edit-${r.rule_id}`}>
                    <PencilSimple size={14} weight="bold" className="inline" /> Edit
                  </button>
                  {!r.is_archived && (
                    <button onClick={() => handleArchive(r.rule_id)} className="text-gray-500 hover:text-red-600" data-testid={`archive-${r.rule_id}`}>
                      <Archive size={14} weight="bold" className="inline" /> Archive
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <RuleModal initial={editing === 'new' ? empty : editing} onClose={() => setEditing(null)} onSave={handleSave} />}
      {simulator && <SimulatorModal initial={simulator} onClose={() => setSimulator(null)} />}
    </div>
  );
};

const RuleModal = ({ initial, onClose, onSave }) => {
  const [f, setF] = useState({
    ...initial,
    required_documents: Array.isArray(initial.required_documents) ? initial.required_documents.join(', ') : (initial.required_documents || ''),
    required_permits: Array.isArray(initial.required_permits) ? initial.required_permits.join(', ') : (initial.required_permits || ''),
    weight_limit_kg: initial.weight_limit_kg ?? '',
    quantity_limit: initial.quantity_limit ?? '',
    effective_date: initial.effective_date || '',
  });
  const change = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur flex items-center justify-center p-4" data-testid="rule-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2"><ShieldStar size={20} weight="duotone" className="text-[#6D28D9]" /> {f.rule_id ? 'Edit Rule' : 'New Rule'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="rule-modal-close"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Origin</Label><CountrySelect value={f.origin} onChange={(v) => change('origin', v)} testId="rm-origin" /></div>
          <div><Label>Destination</Label><CountrySelect value={f.destination} onChange={(v) => change('destination', v)} testId="rm-destination" /></div>
          <div><Label>Category</Label><Input value={f.category} onChange={(e) => change('category', e.target.value)} data-testid="rm-category" /></div>
          <div>
            <Label>Status</Label>
            <select className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm" value={f.status} onChange={(e) => change('status', e.target.value)} data-testid="rm-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><Label>Weight Limit (kg)</Label><Input type="number" step="0.1" value={f.weight_limit_kg} onChange={(e) => change('weight_limit_kg', e.target.value)} data-testid="rm-weight" /></div>
          <div><Label>Quantity Limit</Label><Input type="number" value={f.quantity_limit} onChange={(e) => change('quantity_limit', e.target.value)} data-testid="rm-qty" /></div>
          <div className="col-span-2"><Label>Required Documents (comma-separated)</Label><Input value={f.required_documents} onChange={(e) => change('required_documents', e.target.value)} data-testid="rm-docs" /></div>
          <div className="col-span-2"><Label>Required Permits (comma-separated)</Label><Input value={f.required_permits} onChange={(e) => change('required_permits', e.target.value)} data-testid="rm-permits" /></div>
          <div className="col-span-2"><Label>User-facing Explanation</Label><Textarea value={f.notes} onChange={(e) => change('notes', e.target.value)} rows={2} data-testid="rm-notes" /></div>
          <div className="col-span-2"><Label>Internal Admin Notes</Label><Textarea value={f.internal_notes} onChange={(e) => change('internal_notes', e.target.value)} rows={2} data-testid="rm-internal" /></div>
          <div><Label>Effective Date</Label><Input type="date" value={f.effective_date} onChange={(e) => change('effective_date', e.target.value)} data-testid="rm-effective" /></div>
          <div><Label>Source / Reference</Label><Input value={f.source_reference} onChange={(e) => change('source_reference', e.target.value)} placeholder="Customs URL, gazette" data-testid="rm-source" /></div>
        </div>
        <div className="flex gap-2 mt-6">
          <Button onClick={() => onSave(f)} className="flex-1 dropio-gradient-cta text-white" data-testid="rm-save">
            {f.rule_id ? 'Save Changes' : 'Create Rule'}
          </Button>
          <Button onClick={onClose} className="bg-white text-gray-900 border border-gray-200">Cancel</Button>
        </div>
      </div>
    </div>
  );
};

const SimulatorModal = ({ initial, onClose }) => {
  const [f, setF] = useState(initial);
  const [running, setRunning] = useState(false);
  const change = (k, v) => setF((p) => ({ ...p, [k]: v, result: null }));

  const run = async () => {
    setRunning(true);
    const res = await fetch(`${API}/admin/compliance/simulate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ origin: f.origin, destination: f.destination, category: f.category, weight: parseFloat(f.weight) || 0, quantity: parseInt(f.quantity) || 1 }),
    });
    const data = await res.json();
    setF((p) => ({ ...p, result: data }));
    setRunning(false);
  };

  const tone = {
    GREEN: 'bg-green-50 border-green-200 text-green-700',
    AMBER: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    RED: 'bg-red-50 border-red-200 text-red-700',
  }[f.result?.verdict] || 'bg-gray-50 border-gray-200 text-gray-700';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur flex items-center justify-center p-4" data-testid="simulator-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2"><TestTube size={20} weight="duotone" className="text-[#6D28D9]" /> Route Simulator</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="sim-close"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <Label className="text-xs mb-1 block">Origin</Label>
            <CountrySelect value={f.origin} onChange={(v) => change('origin', v)} allowWildcard={false} testId="sim-origin" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Destination</Label>
            <CountrySelect value={f.destination} onChange={(v) => change('destination', v)} allowWildcard={false} testId="sim-destination" />
          </div>
          <Input placeholder="Category" value={f.category} onChange={(e) => change('category', e.target.value)} data-testid="sim-category" />
          <Input type="number" step="0.1" placeholder="Weight (kg)" value={f.weight} onChange={(e) => change('weight', e.target.value)} data-testid="sim-weight" />
          <Input type="number" placeholder="Quantity" value={f.quantity} onChange={(e) => change('quantity', e.target.value)} data-testid="sim-quantity" />
        </div>
        <Button onClick={run} disabled={running || !f.category} className="w-full dropio-gradient-cta text-white" data-testid="sim-run">
          {running ? 'Running…' : 'Run Simulation'}
        </Button>
        {f.result && (
          <div className={`mt-4 p-4 rounded-lg border ${tone}`} data-testid="sim-result">
            <p className="font-bold mb-1" data-testid="sim-verdict">{f.result.verdict} — {f.result.status}</p>
            {f.result.violations?.length > 0 && <div className="text-sm"><p className="font-semibold">Violations:</p><ul>{f.result.violations.map((v, i) => <li key={i}>• {v}</li>)}</ul></div>}
            {f.result.warnings?.length > 0 && <div className="text-sm mt-1"><p className="font-semibold">Warnings:</p><ul>{f.result.warnings.map((w, i) => <li key={i}>• {w}</li>)}</ul></div>}
            {f.result.required_documents?.length > 0 && <p className="text-xs mt-2">Required docs: {f.result.required_documents.join(', ')}</p>}
            <p className="text-xs mt-2 italic">{f.result.rule_notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RulesEditor;
